/**
 * KPIHeader — Apple IA toolbar.
 * sidebar = places · title = context · Inspector = panel · ••• = actions
 * Mac: no analysis segments. iPhone: Model | Land | Deal | Execution tabs.
 */
import { useState } from 'react';
import {
  Archive,
  CaretDown,
  CopySimple,
  DotsThree,
  Export,
  FilePlus,
  FloppyDisk,
  GearSix,
  ShareNetwork,
  SidebarSimple,
  Trash,
  Wallet,
  MapTrifold,
  ChartLineUp,
  ChartLine,
} from '@phosphor-icons/react';
import { formatPercent } from '../utils/formatters';
import { DeployStatusIndicator } from './DeployStatusIndicator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { EvaLogo } from './EvaLogo';

const VERDICT_STYLE = {
  GO: 'bg-emerald-500 text-white',
  WATCH: 'bg-amber-500 text-white',
  'NO GO': 'bg-red-500 text-white',
};

/**
 * Header Switch CTA cycles these project views (exact order).
 * Cash Tracker is included when the development has an approved baseline;
 * otherwise Switch skips it. Unknown workspaces enter the cycle at P&L.
 */
export const PROJECT_VIEW_CYCLE = ['evaluation', 'land', 'stageFinance', 'cash'];

/** Pure next-workspace helper for the header Switch control. */
export function nextProjectView(workspace, { includeCash = true } = {}) {
  const cycle = includeCash
    ? PROJECT_VIEW_CYCLE
    : PROJECT_VIEW_CYCLE.filter((id) => id !== 'cash');
  const idx = cycle.indexOf(workspace);
  if (idx < 0) return cycle[0];
  return cycle[(idx + 1) % cycle.length];
}

/** Compact Land score label shown next to ROI (not a button). */
export function formatLandScoreMetric(landScore) {
  return `Land ${landScore != null ? landScore : '—'}`;
}

/** Mobile bottom tabs only — Mac uses Inspector for the right panel. */
export const MOBILE_TABS = [
  { id: 'model', label: 'Model' },
  { id: 'land', label: 'Land' },
  { id: 'deal', label: 'Deal' },
  { id: 'execution', label: 'Execution' },
];

export function KPIHeader({
  roiOnEquity,
  verdict,
  reason,
  currentFileName,
  isDraft,
  saveFlash,
  viewOnly = false,
  versionSlot = null,
  summarySlot = null,
  onToggleSidebar,
  sidebarOpen = false,
  inspectorOpen = true,
  onToggleInspector,
  summaryExpanded = false,
  onToggleSummary,
  launched = false,
  workspace = 'evaluation',
  onLaunch,
  onWorkspaceChange,
  onSave,
  onShare,
  onNewVersion,
  onDuplicate,
  onExport,
  onArchive,
  onDelete,
  onOpenSettings,
  lockSlot,
  landScore = null,
  onSwitchView,
}) {
  const landMetric = formatLandScoreMetric(landScore);
  const [titleOpen, setTitleOpen] = useState(false);
  const shortName = currentFileName || 'New Draft';
  const roiLabel = formatPercent(roiOnEquity);
  const verdictCls = VERDICT_STYLE[verdict] || VERDICT_STYLE['NO GO'];

  const titlePopover = (
    <PopoverContent
      align="start"
      className="w-72 p-0 rounded-xl border border-black/10 shadow-lg"
      data-testid="project-title-popover"
    >
      <div className="px-3 py-2 border-b border-black/5">
        <div className="text-[11px] text-gray-500">Scenarios & versions</div>
        <div className="text-[13px] font-semibold text-gray-900 truncate">{shortName}</div>
        {isDraft ? (
          <span className="text-[10px] text-amber-600 font-medium">Draft</span>
        ) : saveFlash ? (
          <span className="text-[10px] text-emerald-600 font-medium">Saved</span>
        ) : null}
      </div>
      <div className="px-3 py-2">
        {versionSlot || <p className="text-[12px] text-gray-400">No versions</p>}
      </div>
      {onNewVersion ? (
        <div className="border-t border-black/5 p-2">
          <button
            type="button"
            onClick={() => {
              setTitleOpen(false);
              onNewVersion();
            }}
            className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-lg text-[13px] font-medium text-blue-600 hover:bg-blue-50"
            data-testid="title-new-version"
          >
            <FilePlus weight="bold" className="w-3.5 h-3.5" />
            New Version
          </button>
        </div>
      ) : null}
    </PopoverContent>
  );

  const commands = (
    <CommandsMenu
      onSave={onSave}
      onShare={onShare}
      onDuplicate={onDuplicate}
      onExport={onExport}
      onToggleSummary={onToggleSummary}
      summaryExpanded={summaryExpanded}
      isDraft={isDraft}
      launched={launched}
      workspace={workspace}
      onLaunch={onLaunch}
      onWorkspaceChange={onWorkspaceChange}
      onOpenSettings={onOpenSettings}
      onArchive={onArchive}
      onDelete={onDelete}
    />
  );

  return (
    <header
      className="sticky top-0 z-50 bg-[#f5f5f7]/90 backdrop-blur-xl border-b border-black/10"
      data-testid="kpi-header"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}
    >
      {/* Mac toolbar */}
      <div className="hidden md:flex h-11 items-center gap-2 px-3 min-w-0" data-testid="apple-toolbar">
        <EvaLogo className="h-5 shrink-0 mr-0.5" />
        {!viewOnly && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className={`w-9 h-9 inline-flex items-center justify-center rounded-lg transition-colors ${
              sidebarOpen ? 'bg-black/10 text-gray-900' : 'text-gray-600 hover:bg-black/5'
            }`}
            aria-label="Toggle projects sidebar"
            aria-pressed={sidebarOpen}
            data-testid="sidebar-toggle-btn"
          >
            <SidebarSimple weight="bold" className="w-5 h-5" />
          </button>
        )}

        <Popover open={titleOpen} onOpenChange={setTitleOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="max-w-[240px] h-9 px-2 inline-flex items-center gap-1 rounded-lg hover:bg-black/5 text-left min-w-0"
              data-testid="project-title-btn"
            >
              <span className="text-[13px] font-semibold text-gray-900 truncate tracking-tight">
                {shortName}
              </span>
              <CaretDown weight="bold" className="w-3 h-3 text-gray-400 shrink-0" />
            </button>
          </PopoverTrigger>
          {titlePopover}
        </Popover>

        {summaryExpanded && summarySlot ? (
          <div
            className="hidden lg:flex items-center gap-3 min-w-0 flex-1 px-2 text-[11px] tabular-nums text-gray-700 overflow-hidden"
            data-testid="toolbar-summary"
          >
            {summarySlot}
          </div>
        ) : (
          <div className="flex-1 min-w-0" />
        )}

        <div className="flex items-center gap-1.5 shrink-0" data-testid="status-badge">
          <span className="text-[15px] font-semibold tabular-nums text-gray-900 tracking-tight" data-testid="financial-score">
            {roiLabel}
          </span>
          <span
            className="text-[13px] font-semibold tabular-nums text-gray-700 tracking-tight"
            data-testid="land-score-chip"
            title={reason || undefined}
          >
            {landMetric}
          </span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${verdictCls}`} data-testid="final-verdict">
            {verdict}
          </span>
          {onSwitchView ? (
            <>
              <span className="text-gray-300 px-0.5" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={onSwitchView}
                className="h-7 px-2.5 rounded-lg text-[12px] font-semibold text-gray-800 border border-black/10 hover:bg-black/5"
                aria-label="Switch project view"
                title="Switch view: P&L → Land Assessment → Stage Finance → Cash Tracker"
                data-testid="workspace-switch-btn"
              >
                Switch
              </button>
            </>
          ) : null}
        </div>

        {!viewOnly && (
          <>
            <button
              type="button"
              onClick={onToggleInspector}
              className={`h-9 px-3 rounded-lg text-[12px] font-medium transition-colors ${
                inspectorOpen
                  ? 'bg-black/10 text-gray-900'
                  : 'text-gray-600 hover:bg-black/5'
              }`}
              aria-pressed={inspectorOpen}
              data-testid="inspector-toggle-btn"
            >
              Inspector
            </button>

            {onSave ? (
              <button
                type="button"
                onClick={onSave}
                className={`h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors ${
                  isDraft
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : saveFlash
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-black/10 text-gray-900 hover:bg-black/15'
                }`}
                data-testid="header-save-btn"
                title={isDraft ? 'Save this project to the cloud' : 'Save now (⌘S)'}
              >
                <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
                {isDraft ? 'Save…' : (saveFlash ? 'Saved' : 'Save')}
              </button>
            ) : null}
            {commands}
            <DeployStatusIndicator />
          </>
        )}

        {lockSlot}
      </div>

      {/* iPhone navigation bar + status */}
      <div className="flex md:hidden flex-col" data-testid="mobile-top-nav">
        <div className="h-11 flex items-center gap-1 px-2">
          <EvaLogo className="h-4 shrink-0 ml-1" />
          {!viewOnly && (
            <button
              type="button"
              onClick={onToggleSidebar}
              className="h-10 px-2 rounded-lg text-[13px] font-medium text-blue-600 min-w-[44px]"
              data-testid="mobile-projects-btn"
            >
              ‹ Projects
            </button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex-1 min-w-0 h-10 inline-flex items-center justify-center gap-1 px-1"
                data-testid="mobile-project-title"
              >
                <span className="text-[13px] font-semibold text-gray-900 truncate">{shortName}</span>
                <CaretDown weight="bold" className="w-3 h-3 text-gray-400 shrink-0" />
              </button>
            </PopoverTrigger>
            {titlePopover}
          </Popover>
                    {onSave ? (
            <button
              type="button"
              onClick={onSave}
              className={`h-9 px-2.5 rounded-lg text-[12px] font-semibold ${
                isDraft ? 'bg-blue-600 text-white' : 'text-blue-600'
              }`}
              data-testid="header-save-btn-mobile"
            >
              {isDraft ? 'Save…' : (saveFlash ? 'Saved' : 'Save')}
            </button>
          ) : null}
          {!viewOnly && commands}
          {lockSlot}
        </div>
        <div
          className="h-8 flex items-center justify-center gap-2 px-3 border-t border-black/5"
          data-testid="mobile-status-row"
        >
          <span className="text-[14px] font-semibold tabular-nums text-gray-900" data-testid="financial-score-mobile">{roiLabel}</span>
          <span
            className="text-[12px] font-semibold tabular-nums text-gray-700"
            data-testid="land-score-chip-mobile"
            title={reason || undefined}
          >
            {landMetric}
          </span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${verdictCls}`} data-testid="final-verdict-mobile">{verdict}</span>
          {onSwitchView ? (
            <>
              <span className="text-gray-300 px-0.5" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={onSwitchView}
                className="h-7 px-2.5 rounded-lg text-[12px] font-semibold text-gray-800 border border-black/10"
                aria-label="Switch project view"
                title="Switch view: P&L → Land Assessment → Stage Finance → Cash Tracker"
                data-testid="workspace-switch-btn-mobile"
              >
                Switch
              </button>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function CommandsMenu({
  onSave,
  onShare,
  onDuplicate,
  onExport,
  onToggleSummary,
  summaryExpanded,
  isDraft = false,
  launched = false,
  workspace = 'evaluation',
  onLaunch,
  onWorkspaceChange,
  onOpenSettings,
  onArchive,
  onDelete,
}) {
  // One Cash Tracker command: approve baseline if needed, then open/close the workspace.
  const canUseCash = Boolean(onWorkspaceChange) && (launched || (!isDraft && onLaunch));
  const cashTitle = isDraft && !launched
    ? 'Save this development first'
    : launched
      ? (workspace === 'cash' ? 'Return to Evaluation' : 'Open Cash Tracker')
      : 'Approve a baseline to open Cash Tracker';

  const handleCashTracker = () => {
    if (launched && onWorkspaceChange) {
      onWorkspaceChange(workspace === 'cash' ? 'evaluation' : 'cash');
      return;
    }
    if (onLaunch) onLaunch();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-gray-600 hover:bg-black/5"
          aria-label="More actions"
          data-testid="commands-menu-btn"
        >
          <DotsThree weight="bold" className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 z-[90] rounded-xl" data-testid="commands-menu">
        {onSave ? (
          <DropdownMenuItem onClick={onSave} data-testid="cmd-save">
            <FloppyDisk weight="bold" className="w-4 h-4 mr-2" /> {isDraft ? 'Save Project…' : 'Save'}
          </DropdownMenuItem>
        ) : null}
        {onShare ? (
          <DropdownMenuItem onClick={onShare} data-testid="cmd-share">
            <ShareNetwork weight="bold" className="w-4 h-4 mr-2" /> Share
          </DropdownMenuItem>
        ) : null}
        {onDuplicate && !isDraft ? (
          <DropdownMenuItem onClick={onDuplicate} data-testid="cmd-duplicate">
            <CopySimple weight="bold" className="w-4 h-4 mr-2" /> Duplicate
          </DropdownMenuItem>
        ) : null}
        {onExport ? (
          <DropdownMenuItem onClick={onExport} data-testid="cmd-export">
            <Export weight="bold" className="w-4 h-4 mr-2" /> Export
          </DropdownMenuItem>
        ) : null}
        {onToggleSummary ? (
          <DropdownMenuItem onClick={onToggleSummary} data-testid="cmd-summary">
            {summaryExpanded ? 'Hide Summary' : 'Show Summary'}
          </DropdownMenuItem>
        ) : null}

        {(onLaunch || onWorkspaceChange) ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onWorkspaceChange?.('evaluation')}
              disabled={workspace === 'evaluation'}
              data-testid="cmd-pnl"
            >
              <ChartLineUp weight="bold" className="w-4 h-4 mr-2" /> P&L
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleCashTracker}
              disabled={!canUseCash}
              title={cashTitle}
              data-testid="cmd-cash-tracker"
            >
              <Wallet weight="bold" className="w-4 h-4 mr-2" /> Cash Tracker
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onWorkspaceChange?.('land')}
              disabled={workspace === 'land'}
              data-testid="cmd-land-assessment"
            >
              <MapTrifold weight="bold" className="w-4 h-4 mr-2" /> Land Assessment
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onWorkspaceChange?.('stageFinance')}
              disabled={workspace === 'stageFinance'}
              data-testid="cmd-stage-finance"
            >
              <ChartLine weight="bold" className="w-4 h-4 mr-2" /> Stage Finance
            </DropdownMenuItem>
          </>
        ) : null}

        <DropdownMenuSeparator />
        {onOpenSettings ? (
          <DropdownMenuItem onClick={onOpenSettings} data-testid="cmd-settings">
            <GearSix weight="bold" className="w-4 h-4 mr-2" /> Project Settings…
          </DropdownMenuItem>
        ) : null}
        {onArchive ? (
          <DropdownMenuItem onClick={onArchive} data-testid="cmd-archive">
            <Archive weight="bold" className="w-4 h-4 mr-2" /> Archive
          </DropdownMenuItem>
        ) : null}
        {onDelete ? (
          <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600" data-testid="cmd-delete">
            <Trash weight="bold" className="w-4 h-4 mr-2" /> Delete
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MobileAnalysisTabBar({ analysisTab, onAnalysisTabChange, hidden = false }) {
  if (hidden) return null;
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#f5f5f7]/95 backdrop-blur-xl border-t border-black/10 pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-tab-bar"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif' }}
    >
      <div className="h-12 flex items-stretch">
        {MOBILE_TABS.map((s) => {
          const active = analysisTab === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onAnalysisTabChange?.(s.id)}
              className={`flex-1 min-h-[44px] text-[11px] font-medium ${
                active ? 'text-blue-600' : 'text-gray-500'
              }`}
              data-testid={`mobile-tab-${s.id}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
